import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { config } from '@/services/config';
import { get, map } from 'lodash';
import { ProspectDetails } from './types';
import { formatDataValue } from '@/helpers/format';
import { Prospect, ProspectTableData } from '@/types/Prospect';
import { createTableHeaders, createTopTableHeaders } from '@/helpers/tables';
import { RootLevel } from '@/types/Directory';
import { PortfolioType } from '@/enums/Global';

export type Getters = {
  getProspectSearchResults(state: State): ProspectTableData;
  getProspectDirectory(state: State): Array<RootLevel>;
  getProspectDetails(state: State): ProspectDetails;
  getProspectPortfoliosIsLoading(state: State): boolean;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getProspectSearchResults: state => {
    const prospectData = state.searchResults;
    const clientTableConfig = get(
      config.layout,
      'tableLayouts.prospectTable',
      {}
    );
    const configTopHeaders = get(clientTableConfig, 'topHeaders', []);
    const configHeaders = get(clientTableConfig, 'mainHeaders', []);

    const items = map(prospectData, item => {
      return {
        status: formatDataValue(item.status),
        clientId: formatDataValue(item.clientId),
        noOfPortfolios: formatDataValue(item.noOfPortfolios),
        aum: formatDataValue(item.aum),
        roa: formatDataValue(item.roa),
        revenue: formatDataValue(item.revenue),
        periodicFees: formatDataValue(item.periodicFees),
        transactionFees: formatDataValue(item.transactionFees),
        businessFinder: formatDataValue(item.businessFinder),
        totalFees: formatDataValue(item.totalFees),
        priceEnforcement: formatDataValue(item.priceEnforcement),
        createdAt: formatDataValue(item.createdAt),
        isDeletable: item.isDeletable.dataValue as boolean,
        isEditable: item.isEditable.dataValue as boolean,
      } as Prospect;
    });

    return {
      items: items,
      headers: createTableHeaders(configHeaders),
      topHeaders: createTopTableHeaders(configTopHeaders),
      totalResultCount: state.searchResultsCount,
    };
  },
  getProspectDirectory: state => {
    return map(state.portfolios, portfolio => {
      return {
        id: formatDataValue(portfolio.scenarioId) as string,
        title: formatDataValue(portfolio.name) as string,
        isBold: false,
        subline: formatDataValue(portfolio.assetsUnderManagement) as string,
        status: formatDataValue(portfolio.simulatedHeatmapStatus) as string,
        children: [],
        isDeletable: portfolio.isDeletable.dataValue,
        approvalStatus: formatDataValue(portfolio.approvalStatus) as string,
        type: PortfolioType.PROSPECT,
      } as RootLevel;
    });
  },
  getProspectDetails: state => {
    const prospectDetails = state.prospectDetails;
    if (prospectDetails) {
      return {
        clientId: formatDataValue(prospectDetails.clientId),
        teamId: formatDataValue(prospectDetails.teamId),
        country: formatDataValue(prospectDetails.country),
        marketId: formatDataValue(prospectDetails.marketId),
      } as ProspectDetails;
    }
    return {} as ProspectDetails;
  },
  getProspectPortfoliosIsLoading: state => state.prospectPortfoliosIsLoading,
};
